/** sessionStorage: remote version we already hard-reloaded for (loop guard). */
export const VERSION_RELOAD_STORAGE_KEY = "steddy:version-reload";

export const VERSION_JSON_PATH = "/version.json";

/** Build id injected by `vite-plugin-app-version`. */
export const APP_VERSION: string = __APP_VERSION__;

export function parseVersionPayload(data: unknown): string | null {
  if (typeof data !== "object" || data === null) return null;
  const version = (data as { version?: unknown }).version;
  if (typeof version !== "string") return null;
  const trimmed = version.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * True when the server is serving a different build than this tab and we have
 * not already reloaded once for that remote version (avoids loops on a bad deploy).
 */
export function shouldReloadForRemoteVersion(
  localVersion: string,
  remoteVersion: string | null,
  alreadyReloadedFor: string | null
): boolean {
  if (!localVersion || !remoteVersion) return false;
  if (remoteVersion === localVersion) return false;
  if (alreadyReloadedFor === remoteVersion) return false;
  return true;
}

export async function fetchRemoteAppVersion(
  fetchImpl: typeof fetch = fetch
): Promise<string | null> {
  try {
    const res = await fetchImpl(VERSION_JSON_PATH, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    return parseVersionPayload(await res.json());
  } catch {
    return null;
  }
}

/**
 * If `/version.json` reports a newer build, hard-reload once for that version.
 * Returns true when a reload was triggered.
 */
export function reloadOnceForNewVersion(
  remoteVersion: string | null,
  localVersion: string = APP_VERSION,
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = defaultSessionStorage(),
  reload: () => void = () => {
    window.location.reload();
  }
): boolean {
  if (!storage) return false;

  if (!remoteVersion || remoteVersion === localVersion) {
    try {
      storage.removeItem(VERSION_RELOAD_STORAGE_KEY);
    } catch {
      // ignore
    }
    return false;
  }

  let alreadyReloadedFor: string | null = null;
  try {
    alreadyReloadedFor = storage.getItem(VERSION_RELOAD_STORAGE_KEY);
  } catch {
    return false;
  }

  if (!shouldReloadForRemoteVersion(localVersion, remoteVersion, alreadyReloadedFor)) {
    return false;
  }

  try {
    storage.setItem(VERSION_RELOAD_STORAGE_KEY, remoteVersion);
  } catch {
    return false;
  }

  reload();
  return true;
}

function defaultSessionStorage(): Pick<Storage, "getItem" | "setItem" | "removeItem"> | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

const reloadBlockers = new Set<() => boolean>();

/**
 * Holds the silent reload while `isBusy` says a reload would lose work (an unsent draft, an
 * answer streaming on screen); the next visibility or focus check tries again. Returns the
 * unregister function.
 */
export function registerReloadBlocker(isBusy: () => boolean): () => void {
  reloadBlockers.add(isBusy);
  return () => {
    reloadBlockers.delete(isBusy);
  };
}

export function isReloadBlocked(): boolean {
  return [...reloadBlockers].some((isBusy) => isBusy());
}
